import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { ShoppingBag, MapPin, Copy } from "lucide-react";
import { HubAddress } from "@/components/HubAddress";

const P_STATUS = {
  // Dès la déclaration, l'achat est considéré comme un colis attendu au Hub (pas d'action client)
  announced: ["ps2.expected", "bg-blue-100 text-blue-700"],
  in_transit: ["ps2.expected", "bg-blue-100 text-blue-700"],
  partial: ["ps2.expected", "bg-amber-100 text-amber-700"],
  received: ["ps2.received", "bg-emerald-600 text-white"],
  consolidated: ["ps2.received", "bg-emerald-600 text-white"],
  unannounced: ["ps.unannounced", "bg-red-100 text-red-700"],
};

export const MyPurchases = ({ filter = "all", embedded = false }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [claim, setClaim] = useState(null);
  const [claimF, setClaimF] = useState({ shop_name: "", order_number: "", description: "" });
  const [edit, setEdit] = useState(null);
  const [editF, setEditF] = useState({});
  const load = () => api.get("/shopship/purchases").then(({ data }) => setItems(data.filter((p) => p.origin !== "goods"))).catch(() => {});
  useEffect(() => { load(); }, []);

  const receivedCount = items.filter((p) => p.status === "received" || p.status === "consolidated").length;
  const totalCount = items.filter((p) => p.status !== "unannounced").length;

  const doClaim = async (yes) => {
    try {
      if (yes) await api.put(`/shopship/purchases/${claim.id}`, { action: "claim", ...claimF });
      else await api.put(`/shopship/purchases/${claim.id}`, { action: "reject" });
      toast.success(yes ? t("sst.confirmed") : t("sst.rejected"));
      setClaim(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("sst.err")); }
  };

  return (
    <div>
      {/* En mode intégré (page Achats en ligne), le titre « Mes achats en ligne » est affiché
          au-dessus des onglets par la page — l'ancien bouton « Acheter & expédier » est remplacé
          par « + Nouvel achat en ligne » en haut de page. */}
      {!embedded && (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-accent" /> {t("ssx.myTitle")}</h3>
          {totalCount > 0 && <p className="text-sm text-muted-foreground" data-testid="purchases-counter">{t("lot.counter").replaceAll("{t}", totalCount).replace("{r}", receivedCount)}</p>}
        </div>
      </div>
      )}

      <div className="mt-4 space-y-2">
        {(() => {
          const DONE_ST = ["shipped", "delivered", "completed", "closed"];
          const shown = items.filter((p) => filter === "all" ? true
            : filter === "todo" ? p.status === "unannounced"
            : filter === "done" ? DONE_ST.includes(p.status)
            : p.status !== "unannounced" && !DONE_ST.includes(p.status));
          if (shown.length === 0) return <p className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="purchases-empty">Aucun achat dans cette catégorie.</p>;
          return shown.map((p) => {
          const [lblK, cls] = P_STATUS[p.status] || [p.status, ""]; const lbl = t(lblK);
          const receivedPk = (p.packages || []).reduce((a, x) => a + (x.cartons || 0), 0);
          return (
            <div key={p.id} data-testid={`purchase-${p.id}`} className="border bg-card p-4 border-border">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <div>
                    <div className="font-heading font-bold">{p.shop_name} <span className="font-normal text-sm text-muted-foreground">· {p.warehouse?.country} ({p.warehouse?.city})</span></div>
                    <div className="text-sm text-muted-foreground">
                      {p.order_number && <>Commande {p.order_number} · </>}
                      {p.tracking_number && <>Tracking {p.tracking_number} · </>}
                      {receivedPk}/{p.packages_expected} colis reçus
                      {p.totals?.weight_kg > 0 && <> · {p.totals.weight_kg} kg</>}
                      {p.dest_country && <> · → {p.dest_country}</>}
                    </div>
                    {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                    {(p.packages || []).some((x) => x.photos?.length) && (
                      <div className="mt-2 flex gap-1.5">
                        {(p.packages || []).flatMap((x) => x.photos || []).slice(0, 4).map((ph, i) => <img key={i} src={ph} alt="" className="h-12 w-12 object-cover border" />)}
                      </div>
                    )}
                  </div>
                </div>
                  <div className="flex flex-col items-end gap-2">
                  <Badge className={`rounded-sm ${cls}`}>{lbl}</Badge>
                  {["announced", "in_transit"].includes(p.status) && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`purchase-edit-${p.id}`}
                      onClick={() => { setEdit(p); setEditF({ shop_name: p.shop_name, order_number: p.order_number || "", tracking_number: p.tracking_number || "", packages_expected: p.packages_expected, description: p.description || "" }); }}>
                      {t("sst.edit")}
                    </Button>
                  )}
                  {p.status !== "unannounced" && (
                    <a href={`https://wa.me/447466398452?text=${encodeURIComponent(`Bonjour Colis2Go, j'ai une question concernant mon achat ${p.shop_name}${p.order_number ? ` (commande ${p.order_number})` : ""}.`)}`}
                      target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`purchase-question-${p.id}`}>{t("q.purchase")}</Button>
                    </a>
                  )}
                  {p.status === "unannounced" && (
                    <div className="flex gap-1.5">
                      <Button size="sm" data-testid={`claim-yes-${p.id}`} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        onClick={() => { setClaim(p); setClaimF({ shop_name: "", order_number: "", description: "" }); }}>{t("sst.myParcel")}</Button>
                      <Button size="sm" variant="outline" data-testid={`claim-no-${p.id}`} className="rounded-sm text-xs text-red-600 border-red-200"
                        onClick={async () => { await api.put(`/shopship/purchases/${p.id}`, { action: "reject" }); toast.success(t("ps.rejected")); load(); }}>{t("ssx.notMine")}</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
          });
        })()}
      </div>

      {/* Edit dialog — modifiable tant que non reçu */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="purchase-edit-dialog">
          <DialogHeader><DialogTitle>{t("sst.editTitle")}</DialogTitle>
            <DialogDescription>{t("sst.editDesc")}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("ssd.shop")}</Label><Input data-testid="edit-shop" className="rounded-sm" value={editF.shop_name || ""} onChange={(e) => setEditF({ ...editF, shop_name: e.target.value })} /></div>
            <div><Label>{t("ssd.order")}</Label><Input data-testid="edit-order" className="rounded-sm" value={editF.order_number || ""} onChange={(e) => setEditF({ ...editF, order_number: e.target.value })} /></div>
            <div><Label>{t("ssd.tracking")}</Label><Input data-testid="edit-tracking" className="rounded-sm" value={editF.tracking_number || ""} onChange={(e) => setEditF({ ...editF, tracking_number: e.target.value })} /></div>
            <div><Label>{t("ssd.expected")}</Label><Input data-testid="edit-count" type="number" className="rounded-sm" value={editF.packages_expected || 1} onChange={(e) => setEditF({ ...editF, packages_expected: e.target.value })} /></div>
            <div className="col-span-2"><Label>{t("ssd.desc")}</Label><Input data-testid="edit-desc" className="rounded-sm" value={editF.description || ""} onChange={(e) => setEditF({ ...editF, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="edit-save" className="rounded-sm bg-accent text-white" onClick={async () => {
              try {
                await api.put(`/shopship/purchases/${edit.id}`, { action: "edit", ...editF, packages_expected: Number(editF.packages_expected) || 0 });
                toast.success(t("sst.editOk")); setEdit(null); load();
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
            }}>{t("sst.editSave")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim dialog */}
      <Dialog open={!!claim} onOpenChange={(v) => !v && setClaim(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("sst.completeTitle")}</DialogTitle>
            <DialogDescription>{t("sst.completeDesc")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("ssd.shop")}</Label><Input data-testid="claim-shop" className="rounded-sm" value={claimF.shop_name} onChange={(e) => setClaimF({ ...claimF, shop_name: e.target.value })} /></div>
            <div><Label>{t("ssd.order")}</Label><Input className="rounded-sm" value={claimF.order_number} onChange={(e) => setClaimF({ ...claimF, order_number: e.target.value })} /></div>
            <div><Label>{t("goods.customDescLabel")}</Label><Input className="rounded-sm" value={claimF.description} onChange={(e) => setClaimF({ ...claimF, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="claim-confirm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doClaim(true)}>{t("sst.confirmMine")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MyAddresses = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/shopship/me").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const copyAddress = (a) => {
    const txt = `${a.recipient}\n${a.address}\n${a.postal_code} ${a.city}\n${a.country}${a.phone ? `\nTél : ${a.phone}` : ""}`;
    navigator.clipboard.writeText(txt).then(() => toast.success(t("ssx.copied")));
  };
  if (!data) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-lg">{t("ssx.addrTitle")}</h3>
          <p className="text-sm text-muted-foreground">Code client : <span className="font-bold text-accent" data-testid="my-client-code">{data.client_code}</span></p>
        </div>
        <Button data-testid="add-address-country" variant="outline" className="rounded-sm" onClick={() => navigate("/shopship")}>{t("sst.addCountry")}</Button>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {data.addresses.map((a) => (
          <div key={a.warehouse_id} data-testid={`address-card-${a.warehouse_id}`} className="border border-border bg-card p-5">
            <div className="font-heading font-bold">{t("sst.myAddr")} {a.country}</div>
            <div className="mt-1 text-sm font-semibold">{a.recipient}</div>
            <div className="mt-1"><HubAddress hub={a} /></div>
            <Button size="sm" className="mt-3 rounded-sm bg-primary text-white gap-1.5" onClick={() => copyAddress(a)}><Copy className="h-4 w-4" /> {t("sst.copy")}</Button>
          </div>
        ))}
        {data.addresses.length === 0 && <p className="text-sm text-muted-foreground">{t("sst.noAddress")}</p>}
      </div>
    </div>
  );
};
